import React, { useEffect, useState } from 'react';
import { Download, Linkedin, Instagram, Edit2, ArrowRight } from 'lucide-react';
import { GenerationResult } from '../types';

interface ResultCardProps {
  result: GenerationResult;
  onReset: () => void;
  onModify: (instruction: string) => void;
}

// Custom X (Twitter) Logo Component since standard icons often default to the bird
const XLogo = ({ size = 20 }: { size?: number }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const ResultCard: React.FC<ResultCardProps> = ({ result, onReset, onModify }) => {
  const [displayImage, setDisplayImage] = useState<string>(result.imageUrl);
  const [isModifying, setIsModifying] = useState(false);
  const [modificationPrompt, setModificationPrompt] = useState('');

  // Apply watermark when the result image changes
  useEffect(() => {
    const applyWatermark = async () => {
      try {
        // Wait for fonts to be ready to ensure correct font rendering
        await document.fonts.ready;

        const img = new Image();
        img.src = result.imageUrl;
        
        // Wait for image to load
        await new Promise((resolve) => { 
            img.onload = resolve;
            img.onerror = resolve; // Handle error gracefully
        });

        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Draw original image
        ctx.drawImage(img, 0, 0);

        // Apply Watermark ONLY if text is provided
        if (result.watermarkText && result.watermarkText.trim().length > 0) {
            // Configure watermark font - scaling based on image size
            const fontSize = Math.max(20, img.width * 0.025);
            ctx.font = `${fontSize}px 'Architects Daughter', cursive, sans-serif`;
            ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'; // Semi-transparent black
            ctx.textAlign = 'right';
            ctx.textBaseline = 'bottom';

            // Draw watermark text
            const padding = fontSize;
            ctx.fillText(result.watermarkText, canvas.width - padding, canvas.height - padding);
        }

        // Update state with watermarked image
        setDisplayImage(canvas.toDataURL('image/jpeg'));
      } catch (error) {
        console.error("Failed to apply watermark:", error);
        // Fallback to original image is already handled by initial state
      }
    };

    applyWatermark();
  }, [result.imageUrl, result.watermarkText]);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = displayImage;
    link.download = `logisketch-${Date.now()}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const shareText = encodeURIComponent(`Check out this sketch: "${result.concept}" #LogiSketch #AIArt`);

  const handleShareTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${shareText}`, '_blank');
  };

  const handleShareLinkedin = () => {
    window.open(`https://www.linkedin.com/feed/?shareActive=true&text=${shareText}`, '_blank');
  };

  const handleShareInstagram = () => {
    handleDownload();
    alert("Image downloaded! You can now post it to Instagram.");
  };

  const handleModifySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (modificationPrompt.trim()) {
        onModify(modificationPrompt);
        setModificationPrompt('');
        setIsModifying(false);
    }
  }

  return (
    <div className="w-full max-w-xl mx-auto animate-in slide-in-from-bottom-8 fade-in duration-700">
      <div className="bg-white p-4 pb-4 rounded-sm border-2 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,0.1)] transition-shadow duration-300 hover:shadow-[8px_8px_4px_0px_rgba(0,0,0,0.2)]">
        
        {/* Image Container (Polaroid style) */}
        <div className="aspect-square w-full bg-gray-50 border-2 border-black mb-4 overflow-hidden relative group">
          <img 
            src={displayImage} 
            alt={result.concept}
            className="w-full h-full object-cover filter contrast-125"
          />
          <div className="absolute inset-0 shadow-inner pointer-events-none"></div>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col gap-4">
            
            {/* Social Shares and Download */}
            <div className="flex items-center justify-between px-2 flex-wrap gap-3">
                <div className="flex gap-3">
                    <button 
                        onClick={handleShareTwitter}
                        className="p-2 rounded-full border-2 border-black hover:bg-black hover:text-white transition-all hover:-translate-y-1"
                        title="Share on X"
                    >
                        <XLogo size={20} />
                    </button>
                    <button 
                        onClick={handleShareInstagram}
                        className="p-2 rounded-full border-2 border-black hover:bg-pink-600 hover:border-pink-600 hover:text-white transition-all hover:-translate-y-1"
                        title="Post on Instagram (Download)"
                    >
                        <Instagram size={20} />
                    </button>
                    <button 
                        onClick={handleShareLinkedin}
                        className="p-2 rounded-full border-2 border-black hover:bg-blue-700 hover:border-blue-700 hover:text-white transition-all hover:-translate-y-1"
                        title="Share on LinkedIn"
                    >
                        <Linkedin size={20} />
                    </button>
                </div>

                <div className="flex gap-3">
                    <button
                        onClick={() => setIsModifying(!isModifying)}
                        className={`flex items-center gap-2 px-4 py-2 border-2 border-black rounded-md text-sm font-bold font-hand transition-all ${isModifying ? 'bg-yellow-100 shadow-inner' : 'bg-white hover:bg-gray-100 hover:-translate-y-1'}`}
                    >
                        <Edit2 size={16} />
                        <span className="hidden sm:inline">Modify</span>
                    </button>

                    <button 
                        onClick={handleDownload}
                        className="flex items-center gap-2 px-6 py-2 bg-black text-white border-2 border-black rounded-md text-sm font-bold font-hand hover:bg-white hover:text-black hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all"
                    >
                        <Download size={16} />
                        Save
                    </button>
                </div>
            </div>

            {/* Modify Input Area */}
            {isModifying && (
                <form onSubmit={handleModifySubmit} className="animate-in slide-in-from-top-2 fade-in duration-300">
                    <div className="relative">
                        <input
                            type="text"
                            value={modificationPrompt}
                            onChange={(e) => setModificationPrompt(e.target.value)}
                            placeholder="e.g., Add a sun, make lines thicker..."
                            className="w-full p-3 pr-12 border-2 border-black rounded-md font-hand focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(253,224,71,0.5)] bg-yellow-50 placeholder-gray-400"
                            autoFocus
                        />
                        <button 
                            type="submit"
                            disabled={!modificationPrompt.trim()}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <ArrowRight size={16} />
                        </button>
                    </div>
                </form>
            )}
        </div>
      </div>
    </div>
  );
};

export default ResultCard;